import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import NarrowSecureLayout from "../../../layout/NarrowSecureLayout.jsx";
import Modal from "../../../components/Modal.jsx";
import { ProjectApi } from "../../../api/authority/generated/index";
import useAuthorityConfiguration from "../../../api/utilities/useAuthorityConfiguration.js";
import { useErrorTranslation } from "../../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import ProjectDomain from "./ProjectDomain.jsx";
import { GlobalContext } from "../../../utils/context/GlobalContextProvider.jsx";
import DomainsConfirmationModal from "./DomainsConfirmationModal.jsx";
import Button from "../../../components/Button.jsx";
import LoadingComponent from "../../../components/LoadingComponent.jsx";

export default function ProjectDomains({ project }) {
    const { t } = useTranslation();
    const { et } = useErrorTranslation();
    const authorityConfiguration = useAuthorityConfiguration();
    const { context } = useContext(GlobalContext);

    // State hooks for managing domains, changes, modals, loading, and price/payment info
    const [domainsWithStats, setDomainsWithStats] = useState([]);
    const [domainsLoading, setDomainsLoading] = useState(true);
    const [changedDomains, setChangedDomains] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [addDomainModalOpened, setAddDomainModalOpened] = useState(false);
    const [addDomainText, setAddDomainText] = useState('');
    const [addDomainLoading, setAddDomainLoading] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [additionalPrice, setAdditionalPrice] = useState(0);
    const [fullPrice, setFullPrice] = useState(0);
    const [domainsToPay, setDomainsToPay] = useState(0);
    const [currencyCode] = useState(project.currencyCode || 'CZK');
    const subscription = project.subscription;

    const handleUpgradePlan = () => {
        navigate(`/project/${project.id}/settings/subscription`);
        onClose();
    };

    // Refs to prevent unwanted repeated operations (for React Strict Mode)
    const isInitialMount = useRef(true);
    const fetchInitiatedRef = useRef(new Map());
    const fetchGenerationRef = useRef(0);

    // For free or missing subscription, only allow one domain to be enabled
    const isFreePackage = !subscription || subscription?.currentPackage?.code === "free";

    // Calculate the count of currently enabled domains (including unsaved state)
    const selectedEnabled = useMemo(() => {
        return Object.values(domainsWithStats).filter((d) =>
            changedDomains[d.projectDomain.hostName] !== undefined
                ? changedDomains[d.projectDomain.hostName]
                : d.projectDomain.isEnabled
        ).length;
    }, [domainsWithStats, changedDomains]);

    // Maximum domains allowed by current subscription
    const maximumDomains = useMemo(() => {
        return subscription?.currentPackage?.maxDomains ?? context.defaultSubscriptionValues.maxDomains;
    }, [project.id, subscription]);

    // Fetches project domains from API and handles possible errors
    const fetchDomains = async () => {
        const generation = ++fetchGenerationRef.current;
        setDomainsWithStats([]);
        setChangedDomains({});
        setDomainsLoading(true);
        try {
            const response = await new ProjectApi(authorityConfiguration).listProjectDomains({
                projectId: project.id,
            });
            if (generation !== fetchGenerationRef.current) return;
            setDomainsWithStats(response || []);
            setDomainsLoading(false);
        } catch (e) {
            if (generation !== fetchGenerationRef.current) return;
            et(e).then((message) => { if (message) toast.error(message); });
        }
    };

    // Loads domains when project is ready or changes
    useEffect(() => {
        if (!project?.id) return;

        const fetchData = async () => {
            // Avoid double-fetching, especially in React StrictMode
            if (isInitialMount.current && fetchInitiatedRef.current.has(project.id)) {
                return;
            }
            if (isInitialMount.current) {
                fetchInitiatedRef.current.set(project.id, true);
            }
            await fetchDomains();
        };

        fetchData();

        if (isInitialMount.current) {
            isInitialMount.current = false;
        }

        // Cleanup (removes completed flag when component unmounts)
        return () => {
            fetchGenerationRef.current += 1;
            fetchInitiatedRef.current.delete(project.id);
        };
    }, [project?.id]);

    // Handles the addition of new domain when loading flag is set
    useEffect(() => {
        if (!addDomainLoading) return;

        new ProjectApi(authorityConfiguration)
            .createOrUpdateProjectDomain({
                projectId: project.id,
                createOrUpdateProjectDomainInput: {
                    isEnabled: false,
                    hostName: addDomainText,
                },
            })
            .then(() => {
                setAddDomainText('');
                setAddDomainModalOpened(false);
                fetchDomains();
            })
            .catch((e) => et(e).then((message) => { if (message) toast.error(message); }))
            .finally(() => setAddDomainLoading(false));
    }, [addDomainLoading, project.id, authorityConfiguration, et, addDomainText]);

    // Handle logic for toggles, allow only one enabled when free or missing subscription
    const handleToggleChange = (hostName, newValue) => {
        if (isFreePackage && newValue) {
            // If any other domain is already enabled, do not allow enabling this one
            const anotherEnabled = Object.values(domainsWithStats)
                .some((d) =>
                    (changedDomains[d.projectDomain.hostName] !== undefined
                        ? changedDomains[d.projectDomain.hostName]
                        : d.projectDomain.isEnabled
                    ) &&
                    d.projectDomain.hostName !== hostName
                );
            if (anotherEnabled) {
                return;
            }
        }
        setChangedDomains((prev) => {
            const newChangedDomains = { ...prev };
            const originalDomain = Object.values(domainsWithStats).find(
                (d) => d.projectDomain.hostName === hostName
            );
            if (originalDomain && originalDomain.projectDomain.isEnabled === newValue) {
                delete newChangedDomains[hostName];
            } else {
                newChangedDomains[hostName] = newValue;
            }
            return newChangedDomains;
        });
    };

    // Save changes: check prices before commit, open confirmation if needed
    const handleSaveChanges = () => {
        setIsSaving(true);

        const domainUpdates = Object.entries(changedDomains).map(([hostName, isEnabled]) => ({
            hostName,
            isEnabled,
        }));

        // Gets possible additional price for requested change
        new ProjectApi(authorityConfiguration)
            .getDomainsStatusChangePrice({
                projectId: project.id,
                createOrUpdateProjectDomainInput: domainUpdates,
            })
            .then((response) => {
                setDomainsToPay(response.domainsToPay);
                const price = response.price;
                if (price > 0) {
                    setAdditionalPrice(price);
                    setFullPrice(response.fullPrice);
                    setIsConfirmationModalOpen(true);
                    setIsSaving(false);
                } else {
                    performDomainUpdate(domainUpdates);
                }
            })
            .catch((e) => {
                et(e).then((message) => { if (message) toast.error(message); });
                setIsSaving(false);
            });
    };

    // Perform the actual update/commit of domain changes
    const performDomainUpdate = (domainUpdates) => {
        new ProjectApi(authorityConfiguration)
            .updateProjectDomainsStatus({
                projectId: project.id,
                createOrUpdateProjectDomainInput: domainUpdates,
            })
            .then(() => {
                setChangedDomains({});
                toast.success(t('project.domains-updated'));
                fetchDomains();
            })
            .catch((e) => et(e).then((message) => { if (message) toast.error(message); }))
            .finally(() => setIsSaving(false));
    };

    // Confirms price-based domain change in modal
    const handleConfirmDomainChange = () => {
        const domainUpdates = Object.entries(changedDomains).map(([hostName, isEnabled]) => ({
            hostName,
            isEnabled,
        }));
        performDomainUpdate(domainUpdates);
        setIsConfirmationModalOpen(false);
    };

    // Cancels any changes (reset state)
    const handleCancelChanges = () => {
        setChangedDomains({});
    };

    // Check if there are any unsaved changes
    const hasChanges = Object.keys(changedDomains).length > 0;

    // State for disabling save button on FREE
    const isSaveDisabled = useMemo(() => {
        if (!hasChanges || isSaving) return true;
        if (isFreePackage) {
            // Allow button only if exactly one domain is enabled
            // Taking into account changes and current state
            return selectedEnabled !== 1;
        }
        return false;
    }, [hasChanges, isSaving, isFreePackage, selectedEnabled]);

    const [primaryDomains, secondaryDomains] = useMemo(() => {
        const sortDomains = (domains) => [...domains].sort((left, right) =>
            right.projectDomainStats.orderCount - left.projectDomainStats.orderCount
            || left.projectDomain.hostName.localeCompare(right.projectDomain.hostName)
        );

        const primary = [];
        const secondary = [];
        domainsWithStats.forEach((domainWithStats) => {
            (domainWithStats.projectDomain.isBuiltin === false || domainWithStats.projectDomainStats.orderCount > 0
                ? primary
                : secondary
            ).push(domainWithStats);
        });

        return [sortDomains(primary), sortDomains(secondary)];
    }, [domainsWithStats]);

    const renderDomain = (domainWithStats) => {
        const isThisDisabled =
            isFreePackage && (
                !(
                    changedDomains[domainWithStats.projectDomain.hostName] !== undefined
                        ? changedDomains[domainWithStats.projectDomain.hostName]
                        : domainWithStats.projectDomain.isEnabled
                ) &&
                selectedEnabled >= 1
            );
        const isEnabled = changedDomains[domainWithStats.projectDomain.hostName] !== undefined
            ? changedDomains[domainWithStats.projectDomain.hostName]
            : domainWithStats.projectDomain.isEnabled;

        return (
            <ProjectDomain
                key={domainWithStats.projectDomain.hostName}
                project={project}
                domainWithStats={domainWithStats}
                onReload={fetchDomains}
                onToggleChange={handleToggleChange}
                isSaving={isSaving}
                isDisabled={isThisDisabled}
                isEnabled={isEnabled}
            />
        );
    };

    return [
        <NarrowSecureLayout
            key={'pd-page-content'}
            title={
                <>
                    {t('project.domains')}
                    {domainsWithStats && (
                        <span className="text-sm text-violet-500 self-end mb-1">
                            {selectedEnabled}/{maximumDomains}
                        </span>
                    )}
                </>
            }
            actions={
                <Button
                    variant="black"
                    onClick={() => setAddDomainModalOpened(true)}
                    disabled={isFreePackage && domainsWithStats.length > 0} // button is always visible but not clickable for free/missing subscription
                >
                    {t('project.add-domain')}
                </Button>
            }
        >
            {isFreePackage && (
                <div
                    className="mb-4 md:-mt-4 w-full h-full flex w-full text-normal text-black justify-between items-center text-left py-3 px-4 rounded-lg bg-violet-100 shadow-sm border border-violet-300 transition"
                >
                    {t('project.no-active-package-text')}
                    <Button type="button" variant="outline-violet" onClick={handleUpgradePlan}>
                        {t('domains.change-tarif')}
                    </Button>
                </div>
            )}

            <div className={'mb-6 rounded-lg border border-violet-200 bg-violet-50 p-4 text-sm text-violet-900 dark:border-violet-900/60 dark:bg-violet-950/30 dark:text-violet-100'}>
                <div className={'font-semibold'}>{t('project.domains-description-title')}</div>
                <div className={'mt-1 text-violet-800 dark:text-violet-200'}>{t('project.domains-description')}</div>
            </div>

            <LoadingComponent loading={domainsLoading}>
            {primaryDomains.length > 0 && (
                <section className={'mb-6'}>
                    <h2 className={'mb-3 text-lg font-semibold'}>{t('project.primary-domains')}</h2>
                    {primaryDomains.map(renderDomain)}
                </section>
            )}

            {secondaryDomains.length > 0 && (
                <section>
                    <h2 className={'mb-3 text-lg font-semibold'}>{t('project.secondary-domains')}</h2>
                    {secondaryDomains.map(renderDomain)}
                </section>
            )}

            {hasChanges && (
                <div className={'flex justify-end mt-6 gap-2'}>
                    <Button
                        variant="outline-black"
                        onClick={handleCancelChanges}
                        disabled={isSaving}
                    >
                        {t('domains.discard-changes')}
                    </Button>
                    <Button
                        variant="black"
                        onClick={handleSaveChanges}
                        loading={isSaving}
                        disabled={isSaveDisabled}
                    >
                        {isSaving ? t('saving') : t('save-changes')}
                    </Button>
                </div>
            )}
            </LoadingComponent>

            {/* Add domain modal */}
            <Modal
                key={'pd-add-domain-modal'}
                className={'w-full lg:w-11/12 max-w-lg'}
                visible={addDomainModalOpened}
                title={t('project.add-domain')}
                setVisible={setAddDomainModalOpened}
                loading={addDomainLoading}
            >
                <div className={'flex flex-col justify-end gap-4 xl:flex-row'}>
                    <div className={'flex justify-center items-center w-full'}>
                        https://
                        <input
                            className={'form-input flex-grow'}
                            type={'text'}
                            value={addDomainText}
                            onChange={(e) => setAddDomainText(e.target.value)}
                        />/
                    </div>
                    <div className={'flex'}>
                        <Button
                            variant="black"
                            className={'ml-auto'}
                            onClick={() => setAddDomainLoading(true)}
                        >
                            {t('project.add-domain')}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Confirmation modal for possible extra price */}
            <DomainsConfirmationModal
                key="pd-confirmation-modal"
                isOpen={isConfirmationModalOpen}
                onConfirm={handleConfirmDomainChange}
                onClose={() => setIsConfirmationModalOpen(false)}
                additionalPrice={additionalPrice}
                fullPrice={fullPrice}
                domainsToPay={domainsToPay}
                currencyCode={currencyCode}
                project={project}
            />
        </NarrowSecureLayout>
    ];
}
